import { Box, Divider, Stack, Typography } from '@mui/material'
import { C1Alert } from 'c1ui'
import { C1Chip } from 'c1ui/components/C1Chip'
import { C1DotLoader } from 'c1ui/components/C1CircularProgress/C1DotLoader'
import { C1CopyText } from 'components/core/copytext/C1CopyText'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { MinimalAPIError } from 'components/core/errors/api'
import { RecommendationInsights } from 'components/core/insights'
import { ExternalLink } from 'components/core/links/external-link'
import { C1MultiUserDisplay } from 'components/core/multi-user/C1MultiUserDisplay'
import { taskHasCancelledOutcome, taskHasDeniedOutcome } from 'components/core/task/outcome/getOutcomeText'
import { TaskPrimaryActionsPendingV2 } from 'components/core/task-list-v2/task-list-v2-components/TaskPrimaryActionsPendingV2'
import { TaskPrimaryActionsV2 } from 'components/core/task-list-v2/task-list-v2-components/TaskPrimaryActionsV2'
import { RelativeTimestamp } from 'components/core/time/display'
import { C1WebhookDisplayWithFetch } from 'components/core/webhooks/WebhookDisplayName'
import { ApprovalOneOfKind, ProvisionOneOfKind } from 'components/pages/policy/type'
import { ExternalTicketStatus } from 'components/pages/task/details/content/TaskApprovalChain'
import { TaskStatusFormattedMessage } from 'components/pages/task/details/content/TaskFormattedMessage'
import { TaskKeyValueMetadataRow } from 'components/pages/task/details/content/TaskKeyValueMetadataRow'
import { getTaskIsAssignedToSystemUser, useGetTaskStatusLabelColor, useGetUserCanActOnTask } from 'components/pages/task/details/content/TaskStatusLabel'
import { canActOn } from 'components/pages/task/state'
import { taskTypeToString } from 'components/pages/task/type'
import { useGetExternalTicket } from 'hooks/useGetExternalTicket'
import { TaskValue } from 'lib/actions/task'
import { TaskTypeString } from 'lib/constants/task'
import { ClipboardList, ClipboardPaste, Clock, Link, Mail, RefreshCw, Ticket } from 'lucide-react'
import { GetPendingActionsByTaskIDsRequest } from 'pbts/c1/api/accessreview/v1/accessreview_pending'
import { TaskActionType } from 'pbts/c1/api/task/v1/task'
import { FC } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { getPendingActionsByTaskIdsApi } from 'redux_toolkit/access_review/pending_actions'
import { TaskPendingActionBanner } from './TaskPendingActionBanner'
import { TaskStepRequiresRequestData } from './TaskStepRequestData'

// if regex matches, return the final segment of the url
// if regex does not match, return full url
const regex = /\/browse\/([A-Z]+-\d+)/
export const linkLabel = (url: string): string => {
    if (!url) return ''
    const match = url.match(regex)

    return match && match[1] ? match[1] : url
}

type TaskCurrentStepProps = {
    task: TaskValue
    hasPendingActions?: boolean
}

export const TaskCurrentStep: FC<TaskCurrentStepProps> = ({ task, hasPendingActions = false }) => {
    const intl = useIntl()
    const color = useGetTaskStatusLabelColor(task)
    const currentStep = task.policy?.current
    const userCanActOnTask = useGetUserCanActOnTask(task)
    const isAgent = currentStep?.instance.oneofKind === 'approval' && currentStep.instance.approval?.approval?.typ.oneofKind === ApprovalOneOfKind.AGENT
    const isTaskAssignedToSystemUser = getTaskIsAssignedToSystemUser(task)
    const accessReviewId = task.accessReview?.id

    // Check if a pending action exists for this task
    // Use task.accessReview?.id directly (not pendingContext) since this component can be used outside access review context
    const { data: pendingActionsResponse } = getPendingActionsByTaskIdsApi.useGetPendingActionsByTaskIDs(
        accessReviewId
            ? GetPendingActionsByTaskIDsRequest.create({
                accessReviewId,
                certifyTaskIds: [task.id],
            })
            : GetPendingActionsByTaskIDsRequest.create({ accessReviewId: '', certifyTaskIds: [] }),
        { actionable: !!accessReviewId && hasPendingActions, evictionAfterSeconds: 0 },
    )

    const pendingState = pendingActionsResponse?.states?.[task.id]

    const hasCurrentStepPendingAction = pendingState?.state.oneofKind === 'unlocked' ? !!pendingState.state.unlocked : false

    if (!currentStep) return null

    // A cancelled or denied task has both a "current step" and an "outcome", so we need to check for the outcome being cancelled or denied,
    // and if so, don't show the current step banner.
    if (taskHasCancelledOutcome(task) || taskHasDeniedOutcome(task)) return null

    return (
        <Box>
            {hasPendingActions && hasCurrentStepPendingAction && <TaskPendingActionBanner task={task} />}
            <C1Alert variant="banner" severity={color} icon={false} sx={{ flex: 1, overflow: 'visible', '& > *': { flex: 1 } }}>
                <Box p={2}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Typography variant="h4"><TaskStatusFormattedMessage task={task} /></Typography>
                        {hasPendingActions ? (
                            <TaskPrimaryActionsPendingV2
                                task={task}
                                neverShowTaskStatusLabel
                                variant="contained"
                            />
                        ) : (
                            <TaskPrimaryActionsV2
                                task={task}
                                neverShowTaskStatusLabel
                                variant="contained"
                            />
                        )}
                    </Stack>

                    <Divider sx={{ my: 2, '&:last-child': { display: 'none' } }} />

                    <Stack spacing={2}>
                        {/* If the task is not assigned to the system user, show the assignees */}
                        {((task.stepApproverIds.length > 0 && !isTaskAssignedToSystemUser))
                            && (
                                <TaskKeyValueMetadataRow
                                    Icon={ClipboardList}
                                    label={intl.formatMessage({ id: 'components_task_dialog_assigned_to', defaultMessage: 'Assigned to' })}
                                    value={<Stack direction="row" alignItems="center">
                                        <C1MultiUserDisplay users={task.stepApprovers || []} />
                                        {
                                            isAgent && <Stack direction="row" alignItems="center" gap={2} ml={2}>
                                                <Typography color="text.secondary">
                                                    <FormattedMessage id="components_task_dialog_assigned_to_working" defaultMessage="Working" />
                                                </Typography>
                                                <C1DotLoader />
                                            </Stack>
                                        }
                                    </Stack>}
                                />
                            )}
                        {currentStep.instance.oneofKind === 'approval' && currentStep.instance.approval?.escalationInstance?.expiresAt !== undefined && (
                            <TaskKeyValueMetadataRow
                                Icon={Clock}
                                label={intl.formatMessage({ id: 'components_task_dialog_escalation', defaultMessage: 'SLA time remaining' })}
                                value={<Typography color="text.secondary">
                                    <RelativeTimestamp
                                        date={currentStep.instance.approval.escalationInstance.expiresAt}
                                    />
                                </Typography>}
                            />
                        )}
                        <TaskProvisionInstructions task={task} />
                        <TaskExternalTicket task={task} />
                        <TaskShowWebhook task={task} />
                        <TaskStepRequiresRequestData task={task} />
                    </Stack>

                    {userCanActOnTask && <RecommendationInsights task={task} />}
                </Box>
            </C1Alert>
        </Box>
    )
}

type TaskShowWebhookProps = {
    task: TaskValue
}

const TaskShowWebhook: FC<TaskShowWebhookProps> = ({ task }) => {
    const intl = useIntl()

    if (task.policy?.current?.instance.oneofKind !== 'approval' || task.policy?.current?.instance.approval?.approval?.typ.oneofKind !== ApprovalOneOfKind.WEBHOOK) {
        return null
    }

    return <Stack direction="row" alignItems="center" justifyContent="space-between">
        <TaskKeyValueMetadataRow
            label={intl.formatMessage({ id: 'components_task_dialog_webhook', defaultMessage: 'Webhook' })}
            value={<C1WebhookDisplayWithFetch id={task.policy.current.instance.approval?.approval?.typ.webhook?.webhookId} />}
        />
    </Stack>
}

type TaskProvisionInstructionsProps = {
    task: TaskValue
}

const TaskProvisionInstructions: FC<TaskProvisionInstructionsProps> = ({ task }) => {
    const current = task.policy?.current?.instance
    const intl = useIntl()
    const taskTypeString = taskTypeToString(task.type)

    const isProvisioning = canActOn(task, TaskActionType.PROVISION_COMPLETE) || canActOn(task, TaskActionType.PROVISION_ERRORED)

    const isExternal = current?.oneofKind === 'provision' && current.provision.provision?.provisionPolicy?.typ.oneofKind === ProvisionOneOfKind.EXTERNAL_TICKET
    if ((current?.oneofKind !== 'provision' || !isProvisioning) && !isExternal) {
        return null
    }

    const provision = current.provision.provision?.provisionPolicy?.typ

    let instructions = ''
    switch (provision?.oneofKind) {
        case ProvisionOneOfKind.MANUAL:
            instructions = provision.manual.instructions
            break
        case ProvisionOneOfKind.EXTERNAL_TICKET:
            instructions = provision.externalTicket.instructions
            break
        default:
    }

    if (instructions === '') {
        if (taskTypeString === TaskTypeString.Revoke) {
            instructions = intl.formatMessage({
                id: 'task_external_ticket_provision_instruction_default_text_revoke',
                defaultMessage: 'This request has been approved and the access needs to be deprovisioned.',
            })
        } else {
            instructions = intl.formatMessage({
                id: 'task_external_ticket_provision_instruction_default_text',
                defaultMessage: 'This request has been approved and the access needs to be provisioned.',
            })
        }
    }

    return <>
        <TaskKeyValueMetadataRow
            Icon={ClipboardPaste}
            label={intl.formatMessage({ id: 'components_task_dialog_requested_for', defaultMessage: 'Instructions' })}
            value={instructions}
        />
        {task.user?.email && !isExternal && <TaskKeyValueMetadataRow
            Icon={Mail}
            label={intl.formatMessage({ id: 'components_task_dialog_requested_for', defaultMessage: 'User email' })}
            value={<C1CopyText value={task.user?.email} />}
        />}
    </>
}

type TaskExternalTicketProps = {
    task: TaskValue
}

const TaskExternalTicket: FC<TaskExternalTicketProps> = ({ task }) => {
    const intl = useIntl()
    const [isLoadingExternalTicket, errorExternalTicket, externalTicket] = useGetExternalTicket({ task })

    if (task.policy?.current?.instance?.oneofKind !== 'provision' || task.policy?.current?.instance?.provision?.provision?.provisionPolicy?.typ.oneofKind !== ProvisionOneOfKind.EXTERNAL_TICKET) {
        return null
    }

    if (isLoadingExternalTicket || !externalTicket?.externalTicket) {
        return null
    }

    if (errorExternalTicket) {
        return <MinimalAPIError err={errorExternalTicket.message} />
    }

    return <>
        {externalTicket?.externalTicket?.externalTicketUrl && <TaskKeyValueMetadataRow
            Icon={Link}
            label={intl.formatMessage({ id: 'components_task_dialog_external_ticket_ticket', defaultMessage: 'External ticket link' })}
            value={<ExternalLink href={externalTicket.externalTicket.externalTicketUrl}>
                {linkLabel(externalTicket.externalTicket.externalTicketUrl)}
            </ExternalLink>
            }
        />}
        {externalTicket.externalTicket.externalTicketStatusDisplayName && <TaskKeyValueMetadataRow
            Icon={Ticket}
            label={<FormattedMessage id="components_task_dialog_external_ticket_ticket_status" defaultMessage="External ticket status" />}
            value={<C1Chip color="info" label={externalTicket.externalTicket.externalTicketStatusDisplayName} />}
        />}
        <TaskKeyValueMetadataRow
            Icon={RefreshCw}
            label={<FormattedMessage id="components_task_dialog_external_ticket_sync_status" defaultMessage="Sync status" />}
            value={<ExternalTicketStatus externalTicket={externalTicket.externalTicket} />}
        />
        <TaskKeyValueMetadataRow
            Icon={Clock}
            label={intl.formatMessage({ id: 'components_task_dialog_external_ticket_last_updated', defaultMessage: 'Last updated' })}
            value={externalTicket.externalTicket.updatedAt ? (
                <RelativeTimestamp date={externalTicket.externalTicket.updatedAt} showAbsoluteDateAfterDays={1} />
            ) : <C1EmptyIndicator />}
        />
    </>
}
